import { AsYouType, parsePhoneNumber } from 'libphonenumber-js/mobile';
import { DEFAULT_COUNTRY, getCountryByCode } from '../components/UISystem/Forms/countryData';

/**
 * Normalize a phone number to E.164 for consistent lookup — the one copy
 * (it was duplicated verbatim in pendingProfile and twice inside
 * syncProfileToSupabase; consolidated on f/simple-merge). The conversion RPC
 * finds the pending record by the verified phone (E.164), so a stored number
 * must match exactly.
 *
 * MIRRORED IN SQL: normalize_phone_digits() (migration 020) applies the same
 * rule server-side — it is the conversion and merge-on-number key. Any change
 * here must change there too, or identity healing silently misses.
 */
export const normalizePhone = (phone) => {
  if (!phone) return null;
  if (phone.startsWith('+')) return phone; // Already E.164
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`; // US number
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return phone; // Return as-is if it can't be normalized
};

/**
 * phoneNumber — the parsing behind PhoneInputField, as pure functions.
 *
 * Extracted so it can be tested: the component is 512 lines of overlay and
 * layout wrapped around three decisions, and it sits on the phone-OTP login
 * path every tester uses. The overlay rendering is fine untested; silently
 * mis-parsing someone's number is not.
 */

/**
 * Which country an E.164 value belongs to. Falls back to the default rather
 * than throwing — a half-typed or malformed number must not break the field
 * the user is typing into.
 *
 * @param {string} value - E.164 ("+19176502256") or anything else
 * @returns {object} a country from countryData
 */
export const countryFromE164 = (value) => {
  if (value && value.startsWith('+')) {
    try {
      const parsed = parsePhoneNumber(value);
      if (parsed && parsed.country) {
        const country = getCountryByCode(parsed.country);
        if (country) return country;
      }
    } catch {
      // Unparseable — fall through to the default
    }
  }
  return DEFAULT_COUNTRY;
};

/**
 * What one keystroke produces: the formatted display string, and the E.164
 * value to store.
 *
 * A partial number has no valid E.164 form, so it's assembled by hand from the
 * country's dial code and the digits typed — otherwise the stored value would
 * stay empty until the number happened to become valid, and a user who typed
 * most of a number and navigated away would lose it.
 *
 * @param {string} input - the raw text now in the field
 * @param {object} country - the selected country
 * @returns {{ display: string, e164: string }}
 */
export const applyPhoneInput = (input, country) => {
  const formatter = new AsYouType(country.code);
  const display = formatter.input(input);

  const phoneNumber = formatter.getNumber();
  if (phoneNumber) return { display, e164: phoneNumber.format('E.164') };
  if (input === '') return { display, e164: '' };

  const digitsOnly = input.replace(/\D/g, '');
  return { display, e164: digitsOnly ? `${country.dialCode}${digitsOnly}` : '' };
};

/**
 * The national part of an E.164 value, for display when the dial code is shown
 * separately. A value that doesn't carry the country's dial code is returned
 * untouched rather than mangled.
 */
export const stripDialCode = (value, country) => {
  if (!value || !country?.dialCode) return value || '';
  return value.startsWith(country.dialCode) ? value.slice(country.dialCode.length) : value;
};

/**
 * A stored E.164 value rendered for display, without its country code —
 * "+19176502256" → "(917) 650-2256".
 *
 * Falls back to the raw value rather than showing nothing: a number that can't
 * be parsed is still the user's number, and blanking the field would look like
 * data loss.
 *
 * @param {string} value - E.164
 * @param {string} fallbackCountryCode - country to format against when the
 *   value doesn't identify one
 * @returns {string}
 */
export const formatE164ForDisplay = (value, fallbackCountryCode) => {
  if (!value || !value.startsWith('+')) return value || '';
  try {
    const parsed = parsePhoneNumber(value);
    if (!parsed) return value;
    const national = parsed.nationalNumber;
    const formatter = new AsYouType(parsed.country || fallbackCountryCode);
    formatter.input(national);
    return formatter.getNumber()?.formatNational() || national;
  } catch {
    return value;
  }
};
